/**
 * ThemeToggle — light/dark theme switch for AI Builder Hub.
 *
 * Behaviour (matching design/project/CitiDev Hub.html + app.jsx):
 * - Reads initial theme from localStorage (key: "hub:theme").
 *   Falls back to the OS prefers-color-scheme if no stored preference,
 *   then defaults to "light".
 * - On mount, applies [data-theme="dark"] to <html> when appropriate.
 * - On toggle, writes the new theme to localStorage AND calls
 *   PUT /api/v1/preferences/theme so the choice persists server-side.
 * - The icon shows the theme a click will switch TO, not the current one:
 *   spaceship icon in light mode (click → blast off into dark mode's
 *   starfield/shooting-star decor, index.css), sun icon in dark mode
 *   (click → switch back to light).
 * - Meets PRD §6 accessibility: keyboard-navigable button with
 *   aria-label, :focus-visible ring from index.css, and honours
 *   prefers-reduced-motion via theme.css.
 *
 * Storage keys
 * ------------
 * localStorage: "hub:theme"  (value: "light" | "dark")
 * API:          PUT /api/v1/preferences/theme  { theme: "light"|"dark" }
 *
 * [STUB] API persistence is best-effort: if the fetch fails (e.g. no auth
 *        session) the toggle still works locally via localStorage.
 */

import type { ReactElement } from "react";
import { useCallback, useEffect, useState } from "react";
import "@/styles/theme.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Theme = "light" | "dark";

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

const THEME_LS_KEY = "hub:theme"; // [STUB] matches design key ("citidev:v1" in prototype)

function loadTheme(): Theme {
  try {
    const raw = localStorage.getItem(THEME_LS_KEY);
    if (raw === "light" || raw === "dark") return raw;
  } catch {
    // localStorage unavailable
  }
  // Default to the warm LIGHT theme (the design's default surface), regardless
  // of OS preference — dark mode stays available via the toggle.
  return "light";
}

function saveTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_LS_KEY, theme);
  } catch {
    // ignore
  }
}

function applyTheme(theme: Theme): void {
  if (theme === "dark") {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

// ---------------------------------------------------------------------------
// API persistence (best-effort; [STUB] requires authenticated session)
// ---------------------------------------------------------------------------

async function persistThemeToApi(theme: Theme): Promise<void> {
  // [STUB] PUT /api/v1/preferences/theme — requires a valid session cookie.
  // Failure is silently swallowed so the toggle still works without a session.
  try {
    await fetch("/api/v1/preferences/theme", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme }),
      credentials: "same-origin",
    });
  } catch {
    // network error or auth failure — ignore, localStorage is source of truth
  }
}

// ---------------------------------------------------------------------------
// useTheme hook (exported for testing / use by App shell)
// ---------------------------------------------------------------------------

export interface UseThemeResult {
  theme: Theme;
  toggle: () => void;
}

export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<Theme>(loadTheme);

  // Apply theme to <html> on mount and whenever theme changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggle = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = current === "dark" ? "light" : "dark";
      saveTheme(next);
      applyTheme(next);
      // Best-effort API persist (fire and forget)
      void persistThemeToApi(next);
      return next;
    });
  }, []);

  return { theme, toggle };
}

// ---------------------------------------------------------------------------
// SVG icon helpers (inline — no external icon library dep needed)
// ---------------------------------------------------------------------------

function SpaceshipIcon(): ReactElement {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2.5c2.4 1.9 3.6 4.8 3.6 8.6 0 2.6-.6 4.9-1.8 7.1l-1.8 1.3-1.8-1.3c-1.2-2.2-1.8-4.5-1.8-7.1 0-3.8 1.2-6.7 3.6-8.6z" />
      <circle cx="12" cy="10.4" r="1.6" />
      <path d="M8.4 14.4 5 16.8l1.2-4.1M15.6 14.4l3.4 2.4-1.2-4.1M10.2 19.5l-.6 2.6h4.8l-.6-2.6" />
    </svg>
  );
}

function SunIcon(): ReactElement {
  return (
    <svg
      width={18}
      height={18}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2v2.6M12 19.4V22M2 12h2.6M19.4 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ThemeToggle component
// ---------------------------------------------------------------------------

interface ThemeToggleProps {
  /** Optional: controlled theme + toggle handler for use by App shell. */
  theme?: Theme;
  onToggle?: () => void;
}

export function ThemeToggle({ theme: controlled, onToggle }: ThemeToggleProps): ReactElement {
  const internal = useTheme();
  const theme = controlled ?? internal.theme;
  const toggle = onToggle ?? internal.toggle;

  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
      title={theme === "dark" ? "Switch to light" : "Switch to dark"}
      aria-pressed={theme === "dark"}
      onClick={toggle}
      data-testid="theme-toggle"
    >
      {/* Icon shows the destination theme, not the current one. */}
      {theme === "dark" ? <SunIcon /> : <SpaceshipIcon />}
    </button>
  );
}
